import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { ArrowRight, Star, Calendar, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";

const HomePage = ({ seoSettings }) => {
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, testimonialsRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/testimonials`),
      ]);
      setServices(servicesRes.data.slice(0, 3));
      setTestimonials(testimonialsRes.data.slice(0, 3));
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  return (
    <div className="pt-20 lg:pt-[118px]">
      <SEO 
        title={seoSettings?.seo_title}
        description={seoSettings?.seo_description}
        keywords={seoSettings?.seo_keywords}
        ogImage={seoSettings?.seo_og_image}
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#F0EBE3] via-white to-[#F7F4F0] py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23C8A882" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          }}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 animate-fade-in" data-testid="hero-title">
              Elevate Your Event with
              <span className="block text-[#8B7355] mt-2">Professional Bartending</span>
            </h1>
            <p className="text-base sm:text-lg text-gray-700 mb-8 max-w-2xl mx-auto animate-fade-in" data-testid="hero-subtitle">
              From intimate gatherings to grand celebrations, we bring premium cocktails and exceptional service directly to your venue.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in">
              <Link to="/contact">
                <Button size="lg" className="btn-primary" data-testid="hero-book-now-button">
                  Book Now <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" variant="outline" className="btn-secondary" data-testid="hero-view-services-button">
                  View Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6" data-testid="stat-events">
              <Calendar className="mx-auto mb-4 text-[#8B7355]" size={48} />
              <h3 className="text-4xl font-bold text-gray-900 mb-2">500+</h3>
              <p className="text-gray-600">Events Served</p>
            </div>
            <div className="text-center p-6" data-testid="stat-clients">
              <Users className="mx-auto mb-4 text-[#8B7355]" size={48} />
              <h3 className="text-4xl font-bold text-gray-900 mb-2">300+</h3>
              <p className="text-gray-600">Happy Clients</p>
            </div>
            <div className="text-center p-6" data-testid="stat-rating">
              <Star className="mx-auto mb-4 text-[#8B7355]" size={48} fill="#8B7355" />
              <h3 className="text-4xl font-bold text-gray-900 mb-2">5.0</h3>
              <p className="text-gray-600">Average Rating</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Services */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-[#F7F4F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-testid="services-section-title">
              Our Services
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Tailored packages to suit every occasion and budget
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={service.id}
                data-testid={`service-card-${index}`}
                className="bg-white rounded-lg overflow-hidden shadow-lg hover-lift"
              >
                <div className="h-48 bg-gradient-to-br from-[#D4C4B0] to-[#8B7355] flex items-center justify-center">
                  <div className="text-6xl">🍸</div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{service.name}</h3>
                  <p className="text-gray-600 mb-4 text-sm">{service.description}</p>
                  <p className="text-2xl font-bold text-[#8B7355] mb-4">{service.price}</p>
                  <Link to="/services">
                    <Button className="w-full" variant="outline" data-testid={`service-learn-more-${index}`}>
                      Learn More
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" className="btn-primary" data-testid="view-all-services-button">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-testid="testimonials-section-title">
              What Our Clients Say
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Don't just take our word for it
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                data-testid={`testimonial-card-${index}`}
                className="bg-gradient-to-br from-[#F7F4F0] to-[#F0EBE3] p-8 rounded-lg shadow-md"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={20} fill="#8B7355" className="text-[#8B7355]" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 text-sm italic">"{testimonial.content}"</p>
                <p className="font-semibold text-gray-900">{testimonial.client_name}</p>
                {testimonial.event_type && (
                  <p className="text-sm text-gray-600">{testimonial.event_type}</p>
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/testimonials">
              <Button size="lg" variant="outline" className="btn-secondary" data-testid="view-all-testimonials-button">
                View All Testimonials
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="cta-title">
            Ready to Make Your Event Unforgettable?
          </h2>
          <p className="text-base sm:text-lg mb-8 opacity-90">
            Get a free quote today and let us help you create an amazing experience
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#8B7355] text-[#1a1a1a] hover:bg-[#A68968]" data-testid="cta-get-quote-button">
              Get Your Free Quote <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
